import { AddOnType } from '../add-on/add-on';
import { AffiliateTerm } from '../affiliation/affiliate-terms/affiliate-terms';
import { OptionalAssets } from '../assets/assets';
import { BlacklistEmail, BlockedBy } from '../blacklist-email';
import { CountryFilter } from '../common/country-filtering';
import { BaseIdentifiers } from '../common/identifiers';
import { UserVerificationType } from '../common/user-verification';
import { WithdrawRule } from '../common/withdraw-rule';
import { ContractType } from '../contract';
import { DashboardLayouts } from '../dashboard-layouts';
import { FeatureFlags } from '../feature-flags/feature-flags';
import { FooterMenuConfig } from '../header-footer-menu/header-footer-menu';
import { HeaderMenuLinkAdmin } from '../header-menu-link/header-menu-link';
import { Languages } from '../languages/languages';
import { NotificationName } from '../notification';
import { SupportedCurrencies } from '../payment';
import { SidebarItem } from '../sidebar/sidebar';
import { TradingSystem } from '../trading-system';
import { I18nEntryArray } from '../translation/translation';
import { UserTermsAndConditionsConfig } from '../user-terms-and-conditions-config/user-terms-and-conditions-config';
import { ApplicationScheduledJob } from './application-scheduled-jobs';

export interface SuggestedComment {
  id: string;
  text: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export type MinimumProgramPriceAsset =
  | OptionalAssets.Forex
  | OptionalAssets.Futures
  | OptionalAssets.DayTrade
  | OptionalAssets.Swing;

export type MinimumProgramPriceByAsset = Partial<
  Record<MinimumProgramPriceAsset, number>
>;

/**
 * Contest terms and conditions with i18n support
 */
export interface ContestTermsAndConditions {
  /** Auto-generated unique identifier for Lokalise */
  id: string;
  /** English content (other languages managed via Lokalise) */
  term: string;
}

export type ApplicationConfig = BaseIdentifiers & {
  dashboardLayout?: DashboardLayouts;
  currencies: CurrencyItem[];
  blacklistEmails: BlacklistEmail[];
  youtubeLiveStream?: YoutubeLiveStream;
  headerMenuLinks?: HeaderMenuLinkAdmin[];
  footerConfig?: FooterMenuConfig;
  companyDetails: CompanyDetails;
  banners?: Banner[];
  userVerificationTriggers?: UserVerificationTrigger[];
  addOns?: AddOnType[];
  scheduledJobs: ApplicationScheduledJob[];
  accountWithdrawRules: WithdrawRule[];
  affiliateWithdrawRules: WithdrawRule[];
  timeZone?: string;
  shouldCreateDemoAccount?: boolean;
  name: string;
  termsAndConditions?: string;
  contestTermsAndConditions?: ContestTermsAndConditions;
  privacyPolicy?: string;
  tradingPracticesUrl?: string;
  website: string;
  featureFlags?: FeatureFlags;
  discordLink?: string;
  tiktokLink?: string;
  linkedinLink?: string;
  showDiscordLink?: boolean;
  restrictAccessToDiscord?: boolean;
  youtubeLink?: string;
  facebookLink?: string;
  twitterLink?: string;
  instagramLink?: string;
  sidebar: SidebarItem[];
  contracts?: ApplicationContract[];
  userBuyingPowerLimitation?: number;
  countryFilter?: CountryFilter;
  achievements?: boolean;
  groupPrograms?: boolean;
  enableCognitoMigration?: boolean;
  tradingSystems?: ApplicationTradingSystem[];
  adminNotification?: AdminNotification[];
  enableMigrationSync?: boolean;
  emailProvider?: EmailProvider;
  minimumWithdrawAmount: number;
  failedPaymentsConfig?: FailedPaymentsConfig;
  userTermsAndConditions?: UserTermsAndConditionsConfig;
  affiliateTermsAndConditions?: AffiliateTerm[];
  affiliateBanners?: AffiliateBanner[];
  affiliateDashboardBanner?: AffiliateDashboardBanner;
  affiliateSplitGroups?: AffiliateSplitGroups;
  i18nArrays?: I18nEntryArray;
  suggestedComments?: SuggestedComment[];
  minimumProgramPrice?: MinimumProgramPriceByAsset;
  interviewTriggerAmount?: number;
};

export type ClientApplicationConfig = Pick<
  ApplicationConfig,
  | 'name'
  | 'dashboardLayout'
  | 'addOns'
  | 'termsAndConditions'
  | 'privacyPolicy'
  | 'tradingPracticesUrl'
  | 'website'
  | 'featureFlags'
  | 'discordLink'
  | 'showDiscordLink'
  | 'youtubeLink'
  | 'facebookLink'
  | 'twitterLink'
  | 'instagramLink'
  | 'tiktokLink'
  | 'linkedinLink'
  | 'sidebar'
  | 'contracts'
  | 'groupPrograms'
  | 'tradingSystems'
  | 'enableMigrationSync'
  | 'banners'
  | 'countryFilter'
  | 'minimumWithdrawAmount'
  | 'userTermsAndConditions'
  | 'contestTermsAndConditions'
  | 'affiliateTermsAndConditions'
  | 'affiliateBanners'
  | 'affiliateDashboardBanner'
  | 'companyDetails'
  | 'suggestedComments'
  | 'i18nArrays'
> & {
  isBlacklisted: boolean;
  blockedBy?: BlockedBy;
  failedPaymentsConfig: FailedPaymentsConfig;
  minimumProgramPrice?: MinimumProgramPriceByAsset;
  interviewTriggerAmount?: number;
};

export interface ApplicationTradingSystem {
  tradingSystem: TradingSystem;
  sellable: boolean;
}

export interface ApplicationContract {
  type: ContractType;
  currentVersion: number;
}

export interface UserVerificationTrigger {
  type: UserVerificationType;
  value: number;
}

export interface CurrencyItem {
  value: SupportedCurrencies;
  countryFilter?: CountryFilter;
}

export interface BannerImages {
  desktopUrl: string;
  tabletUrl?: string;
  mobileUrl?: string;
}

export interface Banner {
  name: string;
  placement: BannerPlacement;
  images: BannerImages;
  link?: string;
  isCheckout?: boolean;
  bannerId?: string;
}

export interface AffiliateBanner {
  id: string;
  images: Partial<Record<Languages, string>>;
}

export type AffiliateDashboardBannerLinkType = 'internal' | 'external';

export interface AffiliateDashboardBanner {
  images?: Partial<Record<Languages, string>>;
  link?: string;
  /** If 'internal', navigates in-app. If 'external' or omitted, opens in new tab. */
  linkType?: AffiliateDashboardBannerLinkType;
}

export enum BannerPlacement {
  Header = 'header',
  Sider = 'sider',
  AffiliateDashboard = 'affiliate-dashboard',
  AccountDashboardRight = 'account-dashboard-right',
  AccountDashboardLeft = 'account-dashboard-left',
}

export interface CompanyDetails {
  // Five Percent Online LTD
  companyName: string;
  // TradeThePool.com
  companyTitle: string;
  city: string;
  street: string;
  address: string;
  country: string;
  zipCode: string;
}

export type YoutubeLiveStreamResponse = {
  enabled: boolean;
  videoUrl: string | null;
  live: boolean;
};

export type YoutubeLiveStream = YoutubeLiveStreamResponse & {
  days: number[];
  startHour: number;
  endHour: number;
  frequency: number;
};

export type AdminNotification = {
  name: NotificationName;
  recipients: string[];
};

export enum EmailProvider {
  MailChimp = 'mailchimp',
  Postmark = 'postmark',
  ActiveCampaign = 'active-campaign',
}

export const ALLOWED_BANNER_FORMAT = ['image/webp', 'image/jpeg', 'image/gif'];

export enum Application {
  TTP = 'ttp',
  THE5ERS = '5ers',
}

export const APPLICATION_BASE_URLS: Record<Application, string> = {
  [Application.THE5ERS]: 'https://the5ers.com',
  [Application.TTP]: 'https://tradethepool.com',
} as const;

export enum WeekDays {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export interface FailedPaymentsConfigVersion {
  version: number;
  updatedAt: Date;
  updatedBy: {
    adminId: string;
    firstName: string;
    lastName: string;
  };
}

export interface FailedPaymentsConfig {
  enabled: boolean;
  failedPaymentThreshold: number;
  weekStartTime: keyof typeof WeekDays;
  timezone: string;
  whitelistedEmails: string[];
  userHubMessage?: string;
  emailAlertRecipient: string[];
  emailAlertCC: string[];
  reactivationEmailSubject?: string;
  reactivationEmailBody?: string;
  emailSender: string;
  versionInfo: FailedPaymentsConfigVersion;
}

/**
 * Affiliate split groups configuration
 * Groups that should use default settings (10% commission, 0% discount)
 * and groups that should have split active (5% commission, 5% discount)
 */
export interface AffiliateSplitGroups {
  /** When false, split functionality is disabled. Default true for backward compat. */
  enabled?: boolean;
  /** Group name that should use default settings (e.g., 'affiliate-default') */
  defaultGroup?: string;
  /** Group name that should have split active (e.g., 'affiliate-split') */
  splitGroup?: string;
}
